// src/store/mfaSlice.ts
/**
 * Admin second-factor state: what the server says about this session, and whether the step-up prompt
 * is on screen.
 *
 * WHY THIS SLICE IMPORTS NO API. `securityApi` imports the store (it reads the bearer token out of
 * `getState()` and dispatches `logout()`), and the store imports this slice's reducer. A single
 * import of `securityApi` from here would close that loop — store → mfaSlice → securityApi → store —
 * and a circular import in a CRA bundle does not fail loudly: one of the three modules simply
 * evaluates with a half-initialised partner and something is `undefined` at run time, usually
 * `store.getState` inside an interceptor. The request half therefore lives in `mfaThunks.ts`, which
 * nothing in the store's own import graph pulls in. The same reasoning keeps `RootState` out of this
 * file: the selectors below take a structural type instead (see `MfaAwareState`).
 *
 * NOTHING HERE IS PERSISTED. `sessionVerified` is a property of one token row
 * (`t_token.c_mfa_verified`), and every login mints fresh rows — a value carried across a login
 * would claim a brand-new session had already passed its second factor. `forToken` exists to make
 * that impossible to get wrong: state that was not loaded for the token currently in `auth` is
 * ignored rather than shown.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loginSuccess, logout } from '../reducers/authSlice';
import { MfaStatus } from '../security/mfaContract';

export type MfaLoadState = 'idle' | 'loading' | 'ready' | 'error';

export interface MfaState {
    loadState: MfaLoadState;
    enabled: boolean;
    sessionVerified: boolean;
    enforced: boolean;
    recoveryCodesRemaining: number | null;
    /** The access token this status was loaded for. Anything else is stale by definition. */
    forToken: string | null;
    loadError: string | null;
    promptOpen: boolean;
    /** A blocking prompt cannot be dismissed: the page behind it is refusing to render until the
     *  session is verified, so an Escape key would leave the reader staring at a placeholder. */
    promptBlocking: boolean;
}

export const initialMfaState: MfaState = {
    loadState: 'idle',
    enabled: false,
    sessionVerified: false,
    enforced: false,
    recoveryCodesRemaining: null,
    forToken: null,
    loadError: null,
    promptOpen: false,
    promptBlocking: false,
};

const mfaSlice = createSlice({
    name: 'mfa',
    initialState: initialMfaState,
    reducers: {
        /** A status request is in flight for `token`. When that is a different token from the one the
         *  current values were loaded for, the values are cleared first — a stale "verified" must not
         *  be readable for even one render of the new session. */
        statusLoading: (state, action: PayloadAction<{ token: string }>) => {
            if (state.forToken !== action.payload.token) {
                state.enabled = initialMfaState.enabled;
                state.sessionVerified = initialMfaState.sessionVerified;
                state.enforced = initialMfaState.enforced;
                state.recoveryCodesRemaining = initialMfaState.recoveryCodesRemaining;
            }
            state.forToken = action.payload.token;
            state.loadState = 'loading';
            state.loadError = null;
        },

        /**
         * The server's answer. The thunk is responsible for not dispatching a result whose token is
         * no longer current; this reducer trusts the token in the payload.
         *
         * A verified session closes the prompt, whatever opened it — that covers the case where the
         * modal is open, the admin verifies in a second tab, and this tab refreshes its status.
         */
        statusLoaded: (state, action: PayloadAction<{ token: string; status: MfaStatus }>) => {
            const { token, status } = action.payload;
            state.forToken = token;
            state.loadState = 'ready';
            state.loadError = null;
            state.enabled = status.enabled;
            state.sessionVerified = status.sessionVerified;
            state.enforced = status.enforced;
            state.recoveryCodesRemaining = status.recoveryCodesRemaining;
            if (status.sessionVerified) {
                state.promptOpen = false;
                state.promptBlocking = false;
            }
        },

        /** The status could not be read. The gate fails OPEN on this — see `StepUpGate` — because a
         *  network blip must never lock the owner out of their own admin pages; the server still
         *  refuses anything that genuinely needs step-up. */
        statusFailed: (state, action: PayloadAction<{ token: string; message: string }>) => {
            state.forToken = action.payload.token;
            state.loadState = 'error';
            state.loadError = action.payload.message;
        },

        /**
         * Show the prompt. `blocking` never downgrades while the prompt is open: a page that will not
         * render until this is done can be joined by a card asking politely, and the polite request
         * must not hand the reader an Escape key that leaves them on a dead page.
         */
        openStepUp: (state, action: PayloadAction<{ blocking: boolean }>) => {
            state.promptBlocking = state.promptOpen
                ? state.promptBlocking || action.payload.blocking
                : action.payload.blocking;
            state.promptOpen = true;
        },

        closeStepUp: (state) => {
            state.promptOpen = false;
            state.promptBlocking = false;
        },

        /** A code was accepted. `enabled` is deliberately not touched: the status refresh that
         *  follows carries it, and inventing it here would paper over a disagreement worth seeing. */
        stepUpVerified: (state) => {
            state.sessionVerified = true;
            state.promptOpen = false;
            state.promptBlocking = false;
        },

        /**
         * The server answered `mfa_required` — dispatched from an interceptor, so it can arrive
         * during any request on any screen. It opens the prompt non-blocking when nothing else has
         * opened it, because the screen underneath is a real screen the reader may want to leave;
         * an already-open blocking prompt keeps its blocking status.
         */
        stepUpRequired: (state) => {
            state.sessionVerified = false;
            if (!state.promptOpen) {
                state.promptOpen = true;
                state.promptBlocking = false;
            }
        },

        /** A fresh set of codes was issued. That only happens through an endpoint that verifies the
         *  second factor first, so enrolment and this session are both known-good afterwards. */
        recoveryCodesReplaced: (state, action: PayloadAction<number>) => {
            state.enabled = true;
            state.sessionVerified = true;
            state.recoveryCodesRemaining = action.payload;
            state.promptOpen = false;
            state.promptBlocking = false;
        },

        /** One code was burned. A no-op when the count is unknown (null) or already zero, so the
         *  displayed number can never go negative or invent a value it was never told. */
        recoveryCodeUsed: (state) => {
            if (typeof state.recoveryCodesRemaining === 'number' && state.recoveryCodesRemaining > 0) {
                state.recoveryCodesRemaining -= 1;
            }
        },
    },

    /**
     * Every action that replaces `auth.accessToken` has to reset this slice, because every field in
     * it describes one specific token. `logout` and `loginSuccess` are the only two today — ANY
     * FUTURE ACTION THAT WRITES `accessToken` (a refresh-token flow, an impersonation switch) MUST BE
     * ADDED HERE, or the new session inherits the old one's `sessionVerified`.
     */
    extraReducers: (builder) => {
        builder.addCase(logout, () => initialMfaState).addCase(loginSuccess, () => initialMfaState);
    },
});

export const {
    statusLoading,
    statusLoaded,
    statusFailed,
    openStepUp,
    closeStepUp,
    stepUpVerified,
    stepUpRequired,
    recoveryCodesReplaced,
    recoveryCodeUsed,
} = mfaSlice.actions;

/**
 * The shape the selectors need, written structurally instead of as `RootState`.
 *
 * Importing `RootState` from `./store` would make the store import this slice and this slice import
 * the store. `RootState` satisfies this type, so `useSelector(selectMfa)` and
 * `useSelector((s: RootState) => selectNeedsStepUp(s))` both typecheck.
 */
export interface MfaAwareState {
    mfa: MfaState;
    auth: { accessToken: string | null };
}

export const selectMfa = (state: MfaAwareState): MfaState => state.mfa;

/** Whether the loaded status describes the session the app is actually using. False right after a
 *  login, and false for a token that was replaced while a request was in flight. */
export const selectMfaIsCurrent = (state: MfaAwareState): boolean =>
    state.mfa.forToken !== null && state.mfa.forToken === state.auth.accessToken;

/** Enrolled, current, and this session has not verified yet — the condition the gate blocks on and
 *  the modal exists to clear. Deliberately false while loading or on error: a page must not be
 *  blocked by a status nobody has read. */
export const selectNeedsStepUp = (state: MfaAwareState): boolean =>
    selectMfaIsCurrent(state) &&
    state.mfa.loadState === 'ready' &&
    state.mfa.enabled &&
    !state.mfa.sessionVerified;

export default mfaSlice.reducer;
